/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import { authorizedApiRequest } from './ApiRequest';
import ProfileModel from '../models/ProfileModel';
import ApiErrorModel from '../models/ApiErrorModel';
import { ConsentType } from '../models/PolicyModel';

const ProfileApi = {
  get: function getProfile(profileId) {
    return authorizedApiRequest('get', `profiles/${profileId}`);
  },
  update: function updateProfile(profileId) {
    return authorizedApiRequest('put', `profiles/${profileId}`);
  },
  updateAvatar: function updateProfileAvatar(profileId) {
    return authorizedApiRequest('put', `profiles/${profileId}/avatar`);
  },
  delAvatar: function updateProfileAvatar(profileId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/avatar`);
  },
  getAll: function getAllProfiles() {
    return authorizedApiRequest('get', 'profiles');
  },
  createDependent: function createDependentProfile() {
    return authorizedApiRequest('post', 'profiles');
  },
  deleteDependent: function deleteDependentProfile(profileId) {
    return authorizedApiRequest('delete', `profiles/${profileId}`);
  },
  getNextProfileColorTheme: function getNextProfileColorTheme() {
    return authorizedApiRequest('get', 'profiles/colorTheme');
  },
  getOwnersForProfile: function getOwnersForProfile(profileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/owners`);
  },
  removeOwnerFromProfile: function removeOwnerFromProfile(profileId, ownerProfileId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/owners/${ownerProfileId}`);
  },
  createProfileExport: function createProfileExport(profileId) {
    return authorizedApiRequest('post', `profiles/${profileId}/profileExports`);
  },
};

const ProfileService = {
  get(profileId, callback) {
    return ProfileApi.get(profileId).end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, new ProfileModel(res.body.data));
    });
  },

  update(profileData, callback) {
    return ProfileApi.update(profileData.id).send(profileData).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  updateAvatar(profileId, photoData, progressCallback, callback) {
    const currentRequest = ProfileApi.updateAvatar(profileId)
      .attach('avatar', photoData)
      .on('progress', progressCallback)
      .end(err => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });

    return {
      abort() {
        currentRequest.abort();
      },
    };
  },

  delAvatar(profileData, callback) {
    return ProfileApi.delAvatar(profileData.id).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  getAll(callback) {
    return ProfileApi.getAll().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const profiles = _.get(res, 'body.data', {});
      callback(apiError, profiles);
    });
  },

  createDependentProfile(profileData, callback) {
    return ProfileApi.createDependent().send(profileData).end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const profile = apiError
        ? new ProfileModel({})
        : new ProfileModel(res.body.data);
      callback(apiError, profile);
    });
  },

  deleteDependentProfile(profileId, callback) {
    return ProfileApi.deleteDependent(profileId).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  getNextProfileColorTheme(callback) {
    return ProfileApi.getNextProfileColorTheme().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const nextColorTheme = _.get(res, 'body.data', {});
      callback(apiError, nextColorTheme);
    });
  },

  getOwnersForProfile(profileId, callback) {
    return ProfileApi.getOwnersForProfile(profileId)
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, _.get(res, 'body.data', []));
      });
  },

  removeOwnerFromProfile(profileId, ownerProfileId, callback) {
    return ProfileApi.removeOwnerFromProfile(profileId, ownerProfileId)
      .end((err) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  getProfileActionsItems: (profileId) => authorizedApiRequest('get', `profiles/${profileId}/actionItems`),
  getNewProfileActionItems: () => {
    return authorizedApiRequest('get', 'profiles/actionItems').then(res => res.body.data);
  },

  provideConsent: (profileId, policyIdList) => {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.PROVIDED,
    }));

    return authorizedApiRequest('post', `profiles/${profileId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  withdrawConsent: (profileId, policyIdList) => {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.WITHHELD,
    }));

    return authorizedApiRequest('post', `profiles/${profileId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  provideGroupConsent: (profileId, groupId, policyIdList) => {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.PROVIDED,
    }));

    return authorizedApiRequest('post', `profiles/${profileId}/groups/${groupId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  withdrawGroupConsent: (profileId, groupId, policyIdList) => {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.WITHHELD,
    }));

    return authorizedApiRequest('post', `profiles/${profileId}/groups/${groupId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  getMedfusionConfig: (profileId) => {
    return authorizedApiRequest('get', `profiles/${profileId}/integrations/medfusion/config`)
      .then((res) => {
        return res.body.data;
      });
  },

  getExistingIntegrations: (profileId) => {
    return authorizedApiRequest('get', `profiles/${profileId}/integrations`)
      .then((res) => {
        return res.body.data;
      });
  },

  refreshMedfusionIntegration: (profileId) => {
    return authorizedApiRequest('put', `profiles/${profileId}/integrations/medfusion/refresh`)
      .then(() => {
        return null;
      });
  },

  getGreenlightSettings: (profileId) => {
    return authorizedApiRequest('get', `profiles/${profileId}/integrations/greenlight/settings`)
      .then((res) => {
        return res.body.data;
      });
  },

  updateGreenlightSettings: (profileId, data) => {
    return authorizedApiRequest('put', `profiles/${profileId}/integrations/greenlight/settings`)
      .send(data)
      .then(() => null);
  },

  downloadExport: async (profileId) => {
    const profileExport = await ProfileApi.createProfileExport(profileId).send();
    const downloadUrl = _.get(profileExport, 'body.data.url');
    return downloadUrl;
  }

};

export default ProfileService;
